import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

const NAV: { to: string; label: string }[] = [
  { to: '/', label: 'Overview' },
  { to: '/budget', label: 'Budget' },
  { to: '/net-worth', label: 'Net Worth' },
  { to: '/projection', label: 'Projection' },
  { to: '/loan-calculator', label: 'Loan Calculator' },
  { to: '/loans', label: 'Loans' },
  { to: '/travel', label: 'Travel' },
  { to: '/aktiver', label: 'Assets' },
  { to: '/receivables', label: 'Receivables' },
  { to: '/saxo', label: 'Saxo Bank' },
  { to: '/settings', label: 'Settings' },
];

/** The nav links, shared verbatim by the desktop sidebar and the mobile drawer. */
function NavLinks({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  return (
    <nav className="flex-1 overflow-y-auto px-2 py-2">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            [
              'block rounded-hv-md px-3 py-2 text-hv-sm transition-colors',
              isActive
                ? 'bg-hv-ai-surface text-hv-ai-text'
                : 'text-hv-text-secondary hover:bg-hv-surface-alt hover:text-hv-text',
            ].join(' ')
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** The brand row at the top of the sidebar / drawer. */
function Brand(): JSX.Element {
  return (
    <div className="flex items-center gap-2 px-4 py-4 text-hv-lg font-medium">
      <span className="inline-block h-2.5 w-2.5 rounded-full bg-hv-ai" aria-hidden />
      Heivol Finance
    </div>
  );
}

function ThemeToggle(): JSX.Element {
  // Minimal ◐ toggle. TODO(phase 9): align with the canonical heivol SiteHeader
  // (design-system/_index.md) before any cross-surface rollout.
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute('data-theme') ?? 'light',
  );
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem('hv-theme', next);
    } catch {
      /* ignore */
    }
    setTheme(next);
  };
  return (
    <button
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      className="rounded-hv-md px-2 py-1 text-hv-text-secondary hover:bg-hv-surface-alt focus-visible:shadow-hv-focus"
    >
      ◐
    </button>
  );
}

export function AppShell({ email, children }: { email: string | null; children: ReactNode }): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex h-full bg-hv-bg text-hv-text">
      {/* Persistent sidebar — md+ only. Hidden on phones; the drawer below replaces it. */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-hv-border bg-hv-surface md:flex">
        <Brand />
        <NavLinks />
      </aside>

      {/* Mobile drawer + overlay — below md only, toggled by the header hamburger. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 h-full w-full bg-black/40"
          />
          <aside
            id="mobile-nav-drawer"
            className="absolute inset-y-0 left-0 flex w-60 max-w-[80%] flex-col border-r border-hv-border bg-hv-surface shadow-hv-lg"
          >
            <Brand />
            <NavLinks onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-hv-border bg-hv-surface px-4 py-3 md:px-6">
          {/* Hamburger — phones only. Opens the drawer. */}
          <button
            type="button"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="mobile-nav-drawer"
            className="rounded-hv-md px-2 py-1 text-hv-lg leading-none text-hv-text-secondary hover:bg-hv-surface-alt focus-visible:shadow-hv-focus md:hidden"
          >
            ☰
          </button>
          <span className="ml-auto text-hv-sm text-hv-text-secondary">{email ?? 'Signed in'}</span>
          <ThemeToggle />
          <button
            onClick={() => { window.location.href = '/auth/logout'; }}
            className="rounded-hv-md border border-hv-border px-3 py-1 text-hv-sm text-hv-text-secondary hover:bg-hv-surface-alt focus-visible:shadow-hv-focus"
          >
            Log out
          </button>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}
